import { AppUrls } from "../app";
import { Controller } from "../controller";
import { Composer } from "../models/composer";
import { Game } from "../models/game";
import { Instrument } from "../models/instrument";
import { Style } from "../models/style";
import { User } from "../models/user";
import { Logger } from "../utils/logger";
import { AutocompleteSearchModelType } from "./auto-complete-search-model-type";

/** Material icon "close" */
const closeButtonSVG: string = `
    <svg xmlns="http://www.w3.org/2000/svg"
        height="24px"
        viewBox="0 -960 960 960"
        width="24px">
        <path
            d="m256-200-56-56 224-224-224-224 56-56 224 224 224-224 56 56-224 224 224 224-56 56-224-224-224 224Z" />
    </svg>
`;

const css: string = `
    .autocomplete-container {
        display: flex;
        flex-direction: column;
        position: relative;
        width: 100%;
        max-width: 300px;
    }

    .autocomplete-input.prepopulated {
        // padding-right: 30px; /* Space for the caret */
        background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="%23808080" class="bi bi-caret-down-fill" viewBox="0 0 16 16"><path d="M7.247 11.14 2.451 6.34a.5.5 0 0 1 .342-.84h10.414a.5.5 0 0 1 .342.84l-4.796 4.8a.5.5 0 0 1-.707 0z"/></svg>');
        background-repeat: no-repeat;
        background-position: right 8px center;
        background-size: 16px;
    }

    .autocomplete-results {
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        background-color: var(--popup-menu-background-color);
        border: 1px solid var(--border-color);
        border-radius: 4px;
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
        z-index: 1000;
        display: none;
        max-height: 200px;
        overflow-y: auto;
    }

    .autocomplete-results.show {
        display: block;
    }

    .autocomplete-result {
        padding: 10px;
        cursor: pointer;
        font-size: 0.8em;
    }

    .autocomplete-result:hover {
        background-color: var(--active-background-color);
    }

    .selected-items {
        margin-top: 10px;
        display: flex;
        flex-direction: column;
        gap: 8px;
        max-width: 300px;
    }

    .selected-item {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
        gap: 4px;
        padding: 5px 10px;
        background-color: var(--popup-menu-background-color);
        border-radius: 4px;
        max-width: 300px;
    }
    .selected-item button {
        margin-left: 8px;
    }

    .remove-item {
        cursor: pointer;
        font-weight: bold;
    }
`;

export class AutoCompleteSearch<T extends Composer | Game | Instrument | Style | User> extends HTMLElement {
    static htmlTag: 'auto-complete-search' = 'auto-complete-search';
    /** Only allow a single selection */
    single: boolean = false;

    url: string | null = null;
    deserializeFn: Function = (a: T) => 'Not implemented';
    resultsVisible: boolean = false;
    loading: boolean = false;
    controller: Controller;
    modelType: AutocompleteSearchModelType | null = null;
    queryTimeout: ReturnType<typeof setTimeout> | undefined = undefined;
    prepopulatedResults: T[] | null = null;
    selectedItems: T[] = [];

    /** @type {Composer[] | Game[] | Instrument[] | Style[] | User[]} */
    searchResults: T[] = [];

    onChange: (() => void) | null = null;

    constructor() {
        super();
        this.controller = new Controller({ logLabel: "AutoCompleteSearch" });
        this.attachShadow({ mode: "open" });
    }

    connectedCallback() {
        const placeholder = this.getAttribute("placeholder") || "Search...";
        const debounceTimeStr = this.getAttribute("debounce-time");
        const debounceTime = debounceTimeStr === null ? 300 : parseInt(debounceTimeStr);
        const singleAttr = this.getAttribute('single');
        if (singleAttr === 'true') {
            this.single = true;
        }

        this.shadowRoot!.innerHTML = `
            <style>
                @import url("${window.location.origin}/static/bundled-styles.css");
                ${css}
            </style>
            <div class="autocomplete-container">
                <input type="text" class="autocomplete-input" placeholder="${placeholder}">
                <div class="autocomplete-results"></div>
                <div class="selected-items"></div>
            </div>
        `;

        const inputElement = this.shadowRoot!.querySelector<HTMLInputElement>(".autocomplete-input")!;

        inputElement.addEventListener("input", () => {
            clearTimeout(this.queryTimeout);

            if (this.prepopulatedResults) {
                this._fetchResults(inputElement.value);
            } else {
                this.queryTimeout = setTimeout(() => {
                    this._fetchResults(inputElement.value);
                }, debounceTime);
            }
        });
    }

    initialize({
        modelType,
        onChange = null,
        prepopulatedResults = null,
        initialSelectedItems = null,
    }: {
        modelType: AutocompleteSearchModelType;
        onChange?: (() => void) | null;
        prepopulatedResults?: T[] | null;
        initialSelectedItems?: T[] | null;
    }): void {
        this.modelType = modelType;
        this.onChange = onChange;
        this.prepopulatedResults = prepopulatedResults;
        this.searchResults = [];

        switch (modelType) {
            case AutocompleteSearchModelType.composer:
                this.url = AppUrls.getComposers;
                this.deserializeFn = Composer.fromObject;
                break;
            case AutocompleteSearchModelType.game:
                this.url = AppUrls.getGames;
                this.deserializeFn = Game.fromObject;
                break;
            case AutocompleteSearchModelType.instrument:
                this.url = AppUrls.getInstruments;
                this.deserializeFn = Instrument.fromObject;
                break;
            case AutocompleteSearchModelType.style:
                this.url = AppUrls.getStyles;
                this.deserializeFn = Style.fromObject;
                break;
            case AutocompleteSearchModelType.tabber:
                this.url = AppUrls.getUsers;
                this.deserializeFn = User.fromObject;
                break;
            default:
                throw new Error("Unsupported model type");
        }

        if (initialSelectedItems) {
            this.selectedItems = initialSelectedItems.map((item) => this.deserializeFn(item));
            this._setState();
        }

        if (this.prepopulatedResults) {
            const inputElement = this.shadowRoot!.querySelector(".autocomplete-input");
            if (inputElement) {
                inputElement.classList.add("prepopulated");
                inputElement.addEventListener("click", (event) => {
                    // Prevent touch events from propagating weirdly
                    event.preventDefault();
                    if (this.resultsVisible) {
                        this.resultsVisible = false;
                    } else {
                        this.searchResults = [...this.prepopulatedResults || []];
                        this.resultsVisible = true;
                    }
                    this._setState();
                });
            }
            else {
                Logger.warn("AutoCompleteSearch", "Could not find the input element .autocomplete-input");
            }
        }
        else {
            document.addEventListener("click", (event: MouseEvent) => {
                const target = event.target as Node;
                if (target && !this.shadowRoot!.contains(target)) {
                    this.resultsVisible = false;
                    this._setState();
                }
            });
            
        }
    }

    _setState(): void {
        const resultsElement = this.shadowRoot!.querySelector(".autocomplete-results");
        const selectedItemsContainer = this.shadowRoot!.querySelector(".selected-items");

        // Toggle the dropdown visibility
        resultsElement?.classList.toggle("show", this.resultsVisible);

        // Clear and rebuild the results dropdown
        if (resultsElement) {
            resultsElement.innerHTML = "";
        }

        if (this.resultsVisible) {
            if (this.searchResults.length === 0 && !this.loading) {
                // Show "No results" message
                const noResultsElement = document.createElement("div");
                noResultsElement.classList.add("autocomplete-result");
                noResultsElement.textContent = "No results";
                resultsElement?.appendChild(noResultsElement);
            } else if (this.searchResults.length > 0) {
                // Populate results
                for (let result of this.searchResults) {
                    const template = this._renderTemplate(result);
                    const resultElement = document.createElement("div");
                    resultElement.innerHTML = template;
                    resultElement.addEventListener("click", () => {
                        this.addItem(result);
                    });
                    resultsElement?.appendChild(resultElement);
                }
            }

            if (this.prepopulatedResults) {
                const closeButton = document.createElement("button");
                closeButton.type = "button";
                closeButton.classList.add("text-button", "primary");
                closeButton.textContent = "Close";
                closeButton.style.width = "100%";
                closeButton.addEventListener("click", () => {
                    this.resultsVisible = false;
                    this._setState();
                });
                resultsElement?.appendChild(closeButton);
            }
        }

        // Rebuild the selected items list
        if (selectedItemsContainer) {
            selectedItemsContainer.innerHTML = "";
        }
        for (let item of this.selectedItems) {
            const itemElement = document.createElement("div");
            itemElement.classList.add("selected-item");
            switch (this.modelType) {
                case AutocompleteSearchModelType.tabber: {
                    const user = item;
                    itemElement.textContent = (user as User)?.username ?? "Unknown";
                    break;
                }
                default: {
                    itemElement.textContent = (item as Composer | Game | Instrument | Style)?.name ?? "Unknown";
                    break;
                }
            }
            const removeButton = document.createElement("button");
            removeButton.classList.add("icon-button", "small", "primary");
            removeButton.innerHTML = closeButtonSVG;
            const ariaAndTitle = this._getRemoveItemButtonAriaLabelAndTitleText(item);
            removeButton.title = ariaAndTitle;
            removeButton.ariaLabel = ariaAndTitle;
            removeButton.addEventListener("click", () => {
                this.removeItem(item);
            });
            itemElement.appendChild(removeButton);
            selectedItemsContainer?.appendChild(itemElement);
        }
    }

    _renderTemplate(item: T): string {
        switch (this.modelType) {
            case AutocompleteSearchModelType.tabber:
                return `
                    <div class="autocomplete-result" data-id="${item.id}">
                        ${(item as User).username}
                    </div>
                `;
            case AutocompleteSearchModelType.game:
                return `
                    <div class="autocomplete-result" data-id="${item.id}">
                        ${(item as Game).name}<br>
                        <span class="font-size-small italics">
                            ${(item as Game).platforms || 'Unknown Platform'}
                        </span>
                    </div>
                `;
            default:
                return `
                    <div class="autocomplete-result" data-id="${item.id}">
                        ${(item as Composer | Instrument | Style).name}
                    </div>
                `;
        }
    }

    _getRemoveItemButtonAriaLabelAndTitleText(item: T): string {
        switch (this.modelType) {
            case AutocompleteSearchModelType.tabber:
                return `Remove ${(item as User).username}`;
            default:
                return `Remove ${(item as Composer | Game | Instrument | Style).name}`;
        }
    }

    async _fetchResults(query: string): Promise<void> {
        if (!query) {
            this.searchResults = [];
            this.resultsVisible = false;
            this._setState();
            return;
        }

        if (this.prepopulatedResults) {
            Logger.info("AutoCompleteSearch", "Searching prepopulated results.");
            const queryLower = query.toLowerCase();
            this.searchResults = this.prepopulatedResults.filter((item) => {
                switch (this.modelType) {
                    case AutocompleteSearchModelType.tabber:
                        return (item as User).username?.toLowerCase().includes(queryLower);
                    default:
                        return (item as Composer | Game | Instrument | Style).name?.toLowerCase().includes(queryLower);
                }
            });

            this.resultsVisible = true;
            this._setState();
        } else {
            if (!this.url || !this.deserializeFn) {
                Logger.info("AutoCompleteSearch", "Setup is incomplete. Results not fetched.");
                this.resultsVisible = false;
                this.searchResults = [];
                this._setState();
                return;
            }

            this.loading = true;
            // Ensure dropdown is visible during loading
            this.resultsVisible = true;
            Logger.info("AutoCompleteSearch", `Fetching results for query: ${query}`);
            this._setState();

            try {
                const response = await fetch(
                    `${this.url}?search=${encodeURIComponent(query)}`,
                    {
                        method: 'GET',
                        credentials: 'same-origin',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                    }
                );
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const data = await response.json();
                this.searchResults = (data.results || []).map(this.deserializeFn);
                // Show dropdown even if no results
                this.resultsVisible = true;
                Logger.info(
                    "AutoCompleteSearch",
                    `Fetched ${this.searchResults.length} results for query: ${query}`
                );
            }
            catch (e) {
                Logger.error(
                    "AutoCompleteSearch",
                    `Error occurred while fetching results. ${e}`
                );
                this.searchResults = [];
                // Show "No results" on error
                this.resultsVisible = true;
            }
            finally {
                this.loading = false;
                this._setState();
            }
        }
    }

    addItem(item: T): void {
        const inputElement = this.shadowRoot!.querySelector<HTMLInputElement>(".autocomplete-input");
        if (inputElement) {
            inputElement.value = "";
        }

        if (this.single) {
            if (this.selectedItems.length == 1 && this.selectedItems[0].id == item.id) {
                return;
            }
            this.selectedItems = [item];
        } else {
            if (this.selectedItems.some((selected) => selected.id === item.id)) {
                this._setState();
                return;
            }
            this.selectedItems.push(item);
        }

        this.resultsVisible = false;
        this._setState();
        Logger.info("AutoCompleteSearch", `Item added: ${JSON.stringify(item)}`);
        if (typeof this.onChange === "function") {
            this.onChange();
        }
    }

    removeItem(item: T): void {
        this.selectedItems = this.selectedItems.filter((selected) => selected.id !== item.id);
        this._setState();
        Logger.info("AutoCompleteSearch", `Item removed: ${JSON.stringify(item)}`);
        if (this.onChange) {
            this.onChange();
        }
    }
}
